import { Controller } from "@hotwired/stimulus"
import { createPrivyClient } from "../lib/privy_client"
import { workStatus, workError, setBusy } from "../lib/work_status"

// Plain-language copy (R16). Raw SDK errors go to the console, never the page.
export const COPY = {
  sendFailed: "We couldn't send a code to that email. Check the address and try again.",
  badCode: "That code didn't match. Check it and try again.",
  setupFailed: "We couldn't finish signing you in. Please try again.",
  unavailable: "Email sign-up isn't working right now. You can try again in a moment, or connect a wallet instead.",
  resend: "Resend code"
}

// The stages of an email sign-in, as the step line names them. A returning
// identity already has its wallet, so that stage ticks straight past.
export const STEPS = [ "Send code", "Check code", "Your wallet", "Sign in" ]

// The email front door (R1): send a one-time code, confirm it, make sure a
// wallet exists for the account, then hand the proof to the server for a
// session. The SDK loads on first use, not on page load (KTD1).
export default class extends Controller {
  static targets = [ "email", "code", "codeStep", "status", "error", "fallback", "sendButton", "confirmButton" ]
  static values = { appId: String, sdkUrl: String, sessionUrl: String }

  connect() {
    // The page's own opening line ("…to get started" / "…to log in") is
    // what the status returns to after an error.
    this.idlePrompt = this.statusTarget.textContent.trim()
    // Until this controller is listening, the forms would really submit;
    // the marker also lets tests wait for the page to be interactive.
    this.element.dataset.privyReady = "true"
  }

  disconnect() {
    if (this.teardown) this.teardown()
  }

  async sendCode(event) {
    event.preventDefault()
    const email = this.emailTarget.value.trim()
    if (!email) return

    this.clearError()
    setBusy(this.sendButtonTarget, true)
    this.setStatus("Sending your code…", "working", 0)

    let privy
    try {
      privy = (await this.client()).privy
    } catch (error) {
      this.log("client unavailable", error)
      this.clientPromise = null
      setBusy(this.sendButtonTarget, false)
      return this.fail(COPY.unavailable, { fallback: true })
    }

    // A live session from an earlier login (another email, or a finished
    // run) makes the SDK refuse a fresh login — which would surface as a
    // code that "didn't match". Starting here always starts clean; the
    // identity and its wallet live with Privy, so nothing is lost.
    try { await privy.auth.logout() } catch { /* nothing to log out */ }

    try {
      await privy.auth.email.sendCode(email)
    } catch (error) {
      this.log("sendCode failed", error)
      setBusy(this.sendButtonTarget, false)
      return this.fail(COPY.sendFailed)
    }

    setBusy(this.sendButtonTarget, false)
    this.codeStepTarget.hidden = false
    this.codeTarget.focus()
    // The next move is the saver's: the code is in their inbox.
    this.setStatus(`We emailed a code to ${email}. Enter it below.`, "waiting", 1)
    this.offerResend()
  }

  // Once a code is on its way, Confirm is the next tap: the send button
  // steps back to a quiet "Resend code" so the two do not compete (the first
  // outside tester, 2026-10-24).
  offerResend() {
    const button = this.sendButtonTarget
    if (button.dataset.resend === "true") return
    button.dataset.resend = "true"
    button.textContent = COPY.resend
    button.className = "chip min-h-11 px-4 text-sm cursor-pointer disabled:opacity-50 self-center"
  }

  async confirmCode(event) {
    event.preventDefault()
    const code = this.codeTarget.value.trim()
    if (!code) return

    this.clearError()
    setBusy(this.confirmButtonTarget, true)
    this.setStatus("Checking your code…", "working", 1)

    let sdk, privy, user
    try {
      ({ sdk, privy } = await this.client())
    } catch (error) {
      this.log("client unavailable", error)
      this.clientPromise = null
      setBusy(this.confirmButtonTarget, false)
      return this.fail(COPY.unavailable, { fallback: true })
    }

    try {
      ({ user } = await privy.auth.email.loginWithCode(this.emailTarget.value.trim(), code))
    } catch (error) {
      this.log("loginWithCode failed", error)
      setBusy(this.confirmButtonTarget, false)
      return this.fail(COPY.badCode)
    }

    try {
      // Returning identities keep the wallet they already have; only a first
      // login provisions one.
      let account = sdk.getUserEmbeddedSolanaWallet(user)
      if (!account) {
        this.setStatus("Setting up your wallet…", "working", 2)
        const result = await privy.embeddedWallet.createSolana()
        account = sdk.getUserEmbeddedSolanaWallet(result.user)
      }
      if (!account) throw new Error("no wallet after createSolana")

      this.setStatus("Signing you in…", "working", 3)
      const payload = await this.postSession(privy)
      this.setStatus("Done", "done", 3)
      window.location.assign(payload.redirect_to || "/")
    } catch (error) {
      this.log("sign-in failed", error)
      setBusy(this.confirmButtonTarget, false)
      this.fail(error.userMessage || COPY.setupFailed)
    }
  }

  // One client per page visit; a failed bootstrap is retried on the next tap.
  client() {
    this.clientPromise ||= createPrivyClient({
      sdkUrl: this.sdkUrlValue,
      appId: this.appIdValue,
      container: this.element
    }).then((client) => {
      this.teardown = client.teardown
      return client
    })
    return this.clientPromise
  }

  async postSession(privy) {
    const response = await fetch(this.sessionUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
      },
      body: JSON.stringify({
        access_token: await privy.getAccessToken(),
        identity_token: await privy.getIdentityToken()
      })
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      const error = new Error(`session request failed: ${response.status}`)
      error.userMessage = payload.error
      throw error
    }
    return payload
  }

  // The status line and its step line (lib/work_status.js); no step means
  // the resting prompt, with no step line.
  setStatus(text, state = "idle", step = null) {
    workStatus(this.statusTarget, text, state, step === null ? {} : { steps: STEPS, step })
  }

  fail(copy, { fallback = false } = {}) {
    this.setStatus(this.idlePrompt)
    workError(this.errorTarget, copy)
    if (fallback) this.fallbackTarget.hidden = false
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  log(message, error) {
    console.debug(`[${this.identifier}] ${message}`, error)
  }
}
